// 牌桌中央資訊面板：MOAI CITY logo + 「X風X局 N底 N台」
// 對應 PRD §4.4 中央裝飾（取代舊版純文字 '東風 ・ 至尊麻將王'）
//
// === 使用圖片 ===
// - public/assets/ui/table-logo.png（key: TABLE_LOGO_KEY，216×72）— 主圖案
// - public/assets/ui/table-text.png（key: TABLE_TEXT_KEY，4×4 sprite）— row 1 東南西北 / row 3 風局底台
// - public/assets/ui/table-number.png（key: TABLE_NUMBER_KEY，2×5 sprite）— 0–9 數字
// 文字 / 數字素材原圖都是白底（PNG，透明背景 + 白色內容），渲染時 tint 黑色 + 半透明
// 讓資訊面板像「印」在牌桌上、不搶牌面注意力。
// Logo 已是設計過的彩色素材，原色呈現，不上 tint / alpha。
//
// === 排版 ===
//   Row 1：logo（置中）
//   Row 2：[圈風字][風][輪局風字][局][數字...][底][數字...][台]（置中對齊）
// 兩列以 LOGO_TO_TEXT_GAP 區隔，整組以 (centerX, centerY) 為幾何中心擺位。

import { Wind, type TWind } from '@core/types';
import {
  TABLE_LOGO_KEY,
  TABLE_NUMBER_KEY,
  TABLE_TEXT_KEY,
  TableTextFrame,
  type TTableTextFrame,
} from '@scenes/boot-scene';
import type Phaser from 'phaser';

import { UIDepth } from './ui-depths';

// ============================================================================
// 視覺常數
// ============================================================================

const TINT_COLOR = 0x000000; // 統一染色：黑
const TINT_ALPHA = 0.5; // 統一透明度（半透明融入桌面）
const DEPTH = UIDepth.TableInfo; // 高於桌面背景 / 風字徽章 (-50)，但低於所有牌面 sprite (預設 0)

const LOGO_SCALE = 1.5; // logo 縮放（216×72 native）
const TEXT_SCALE = 1; // 風字 / 風局底台 文字縮放（54×54 native → 21.6×21.6）
const NUMBER_SCALE = 1; // 數字縮放（27×54 native → 10.8×21.6，跟文字同高）

const LOGO_TO_TEXT_GAP = 12; // logo 底邊 → 文字列頂邊 的間距（post-scale px）
const CHAR_GAP = 2; // 文字 / 數字 sprite 之間的水平間距（post-scale px）
const SEGMENT_GAP = 16; // 「X風X局」與「N底」、「N底」與「N台」之間的區段間距

// TWind → table-text row 1 (normal) frame index
const WIND_FRAME: Record<TWind, TTableTextFrame> = {
  east: TableTextFrame.EastNormal,
  south: TableTextFrame.SouthNormal,
  west: TableTextFrame.WestNormal,
  north: TableTextFrame.NorthNormal,
};

// ============================================================================
// Config
// ============================================================================

/**
 * 「X風X局」的局風（第幾局；1=東 / 2=南 / 3=西 / 4=北）。
 * 在四圈麻將中：圈風固定（如東風戰），局風隨莊家輪換移動。
 */
export type TRoundIndex = 1 | 2 | 3 | 4;

export type TTableInfoConfig = {
  /** 整組面板的中心 X */
  readonly centerX: number;
  /** 整組面板的中心 Y */
  readonly centerY: number;
  /** 圈風（"X風X局" 中第 1 個風字；e.g. 東風戰 = Wind.East） */
  readonly prevailingWind: TWind;
  /** 局風（"X風X局" 中第 2 個風字；對應第幾局，1=東 / 2=南 / 3=西 / 4=北） */
  readonly roundIndex: TRoundIndex;
  /** 底分（顯示為 "N底"） */
  readonly basePoints: number;
  /** 台分（顯示為 "N台"） */
  readonly fanPoints: number;
};

const ROUND_INDEX_TO_WIND: Record<TRoundIndex, TWind> = {
  1: Wind.East,
  2: Wind.South,
  3: Wind.West,
  4: Wind.North,
};

// ============================================================================
// CTableInfo
// ============================================================================

export class CTableInfo {
  private readonly objects: Phaser.GameObjects.GameObject[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private config: TTableInfoConfig
  ) {
    this.render();
  }

  /** 整批 destroy 已渲染物件，依新 config 重畫。 */
  update(partial: Partial<TTableInfoConfig>): void {
    this.config = { ...this.config, ...partial };
    this.destroy();
    this.render();
  }

  /** 移除所有子物件（給 scene shutdown 或外部要替換用）。 */
  destroy(): void {
    for (const o of this.objects) o.destroy();
    this.objects.length = 0;
  }

  // --------------------------------------------------------------------------
  // private
  // --------------------------------------------------------------------------

  private render(): void {
    const { centerX, centerY } = this.config;

    // ---- 1. Logo ----
    const logoTex = this.scene.textures.get(TABLE_LOGO_KEY).getSourceImage();
    const logoH = logoTex.height * LOGO_SCALE;

    // ---- 2. 預先計算文字列總寬 ----
    const charW = 54 * TEXT_SCALE; // 文字 sprite（table-text）後寬
    const digitW = 27 * NUMBER_SCALE; // 數字 sprite（table-number）後寬

    const baseDigits = String(Math.max(0, Math.floor(this.config.basePoints))).split('');
    const fanDigits = String(Math.max(0, Math.floor(this.config.fanPoints))).split('');

    const widthOfChars = (n: number) => n * charW + Math.max(0, n - 1) * CHAR_GAP;
    const widthOfDigits = (n: number) => n * digitW + Math.max(0, n - 1) * CHAR_GAP;

    // [圈風][風][局風][局] = 4 個 char
    const segRoundW = widthOfChars(4);
    // [數字...][底] = digits + 1 個 char
    const segBaseW = widthOfDigits(baseDigits.length) + CHAR_GAP + charW;
    // [數字...][台] = digits + 1 個 char
    const segFanW = widthOfDigits(fanDigits.length) + CHAR_GAP + charW;
    const textRowWidth = segRoundW + SEGMENT_GAP + segBaseW + SEGMENT_GAP + segFanW;

    // ---- 3. 整組高度 → 把 logo / textRow 上下擺位 ----
    const textRowH = charW; // 文字列高 = 一個字的縮放後尺寸
    const totalH = logoH + LOGO_TO_TEXT_GAP + textRowH;

    const logoY = centerY - totalH / 2 + logoH / 2;
    const textY = centerY + totalH / 2 - textRowH / 2;

    // ---- 4. 放 logo（原色呈現，不上 tint / alpha） ----
    const logo = this.scene.add
      .image(centerX, logoY, TABLE_LOGO_KEY)
      .setScale(LOGO_SCALE)
      .setDepth(DEPTH);
    this.objects.push(logo);

    // ---- 5. 放文字列（左對齊鋪排，整列以 centerX 置中） ----
    let cursorX = centerX - textRowWidth / 2; // 文字列「最左緣」X

    // 5-1. 圈風字
    cursorX = this.placeTextChar(cursorX, textY, WIND_FRAME[this.config.prevailingWind]);
    // 5-2. 「風」
    cursorX = this.placeTextChar(cursorX, textY, TableTextFrame.Wind);
    // 5-3. 局風字（從 roundIndex 推回 wind 字）
    const roundWind = ROUND_INDEX_TO_WIND[this.config.roundIndex];
    cursorX = this.placeTextChar(cursorX, textY, WIND_FRAME[roundWind]);
    // 5-4. 「局」
    cursorX = this.placeTextChar(cursorX, textY, TableTextFrame.Round);
    // 段落間距
    cursorX += SEGMENT_GAP - CHAR_GAP;
    // 5-5. 底分數字
    for (const d of baseDigits) {
      cursorX = this.placeDigit(cursorX, textY, Number(d));
    }
    // 5-6. 「底」
    cursorX = this.placeTextChar(cursorX, textY, TableTextFrame.Base);
    cursorX += SEGMENT_GAP - CHAR_GAP;
    // 5-7. 台分數字
    for (const d of fanDigits) {
      cursorX = this.placeDigit(cursorX, textY, Number(d));
    }
    // 5-8. 「台」
    this.placeTextChar(cursorX, textY, TableTextFrame.Fan);
  }

  /** 放一張 table-text 字（origin 左中），回傳下一個 cursor X。 */
  private placeTextChar(x: number, y: number, frame: TTableTextFrame): number {
    const img = this.scene.add
      .image(x, y, TABLE_TEXT_KEY, frame)
      .setOrigin(0, 0.5)
      .setScale(TEXT_SCALE)
      .setTint(TINT_COLOR)
      .setAlpha(TINT_ALPHA)
      .setDepth(DEPTH);
    this.objects.push(img);
    return x + 54 * TEXT_SCALE + CHAR_GAP;
  }

  /** 放一張 table-number 數字（origin 左中），回傳下一個 cursor X。 */
  private placeDigit(x: number, y: number, digit: number): number {
    const img = this.scene.add
      .image(x, y, TABLE_NUMBER_KEY, digit)
      .setOrigin(0, 0.5)
      .setScale(NUMBER_SCALE)
      .setTint(TINT_COLOR)
      .setAlpha(TINT_ALPHA)
      .setDepth(DEPTH);
    this.objects.push(img);
    return x + 27 * NUMBER_SCALE + CHAR_GAP;
  }
}
